/**
 * ChatMessage - One message in the ring chat (GB edition).
 * Shows sender, timestamp, text content, and a play button for audio.
 * Pixelated GB styling: no rounded corners, only the GB palette.
 */
import { useState, useRef, useEffect } from 'react';
import { fetchAudio, base64ToBlob, type ChatMessage as Msg } from '@/lib/ringChat';

interface ChatMessageProps {
  msg: Msg;
  ringId: string;
  side: 'left' | 'right';
}

function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

export function ChatMessage({ msg, ringId, side }: ChatMessageProps) {
  const isMine = side === 'right';
  // GB differentiation via inversion: my side = inverted (dark bg, light text),
  // their side = light bg + dark border (same as gb-dialog).
  const label = msg.sender === 'visitor' ? 'VISITANTE' : 'RESIDENTE';
  const time = formatTime(msg.created_at);

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: isMine ? 'flex-end' : 'flex-start',
      }}
    >
      <div
        aria-label={`${label} a las ${time}${msg.text ? `: ${msg.text}` : msg.has_audio ? ': audio' : ''}`}
        style={{
          maxWidth: '85%',
          padding: '4px 6px 5px',
          border: '2px solid var(--gb-dark)',
          background: isMine ? 'var(--gb-dark)' : 'var(--gb-bg)',
          color: isMine ? 'var(--gb-bg)' : 'var(--gb-dark)',
          fontFamily: 'var(--pixel)',
        }}
      >
        <div
          style={{
            fontSize: 5,
            letterSpacing: 0.4,
            marginBottom: 2,
            color: isMine ? 'var(--gb-bg)' : 'var(--gb-mid)',
            textTransform: 'uppercase',
          }}
        >
          {label} · {time}
        </div>
        {msg.text && (
          <div
            style={{
              fontSize: 8,
              lineHeight: 1.3,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              color: isMine ? 'var(--gb-bg)' : 'var(--gb-dark)',
            }}
          >
            {msg.text}
          </div>
        )}
        {msg.has_audio && (
          <AudioPlayer ringId={ringId} msgId={msg.id} mime={msg.audio_mime} inverted={isMine} />
        )}
      </div>
    </div>
  );
}

function AudioPlayer({
  ringId,
  msgId,
  mime,
  inverted,
}: {
  ringId: string;
  msgId: string;
  mime: string | null;
  inverted: boolean;
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const urlRef = useRef<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errored, setErrored] = useState(false);

  const toggle = async () => {
    if (audioRef.current && !loading && !errored) {
      if (playing) {
        audioRef.current.pause();
        setPlaying(false);
      } else {
        try {
          await audioRef.current.play();
          setPlaying(true);
        } catch {
          setErrored(true);
        }
      }
      return;
    }
    if (loading) return;
    setLoading(true);
    setErrored(false);
    try {
      const b64 = await fetchAudio(ringId, msgId);
      const blob = base64ToBlob(b64, mime || 'audio/webm');
      const url = URL.createObjectURL(blob);
      urlRef.current = url;
      // Create the audio element FIRST and wire error/ended handlers
      // BEFORE assigning src. Otherwise the error event can fire from the
      // src-loading phase before onerror is attached, and we'd miss it
      // (matches the AudioPlayer hardening in app/src/components/ChatMessage.tsx
      // that fixed the sender's own audio replay — same race here).
      const audio = new Audio();
      audio.preload = 'auto';
      audio.onended = () => setPlaying(false);
      audio.onerror = () => { setPlaying(false); setErrored(true); };
      audio.src = url;
      audioRef.current = audio;
      // Wait for the audio to be decodable before calling play(). On
      // mobile Safari (especially iOS PWA) calling play() immediately
      // after src assignment can fire onerror before the audio has had
      // a chance to load — even though the bytes are valid. Timeout
      // fallback so a slow load doesn't hang the spinner forever.
      await new Promise<void>((resolve) => {
        let settled = false;
        const onReady = () => {
          if (settled) return;
          settled = true;
          resolve();
        };
        if (audio.readyState >= 3 /* HAVE_FUTURE_DATA */) {
          resolve();
          return;
        }
        audio.addEventListener('canplaythrough', onReady, { once: true });
        audio.addEventListener('canplay', onReady, { once: true });
        window.setTimeout(() => {
          if (settled) return;
          settled = true;
          resolve();
        }, 5000);
      });
      await audio.play();
      setPlaying(true);
    } catch (err) {
      console.warn('[AudioPlayer] playback failed', { msgId, mime, err });
      setErrored(true);
    } finally {
      setLoading(false);
    }
  };

  // Release the blob URL + pause audio when the message scrolls offscreen
  // or the chat unmounts. Without this, every audio you played stays in
  // memory until the page reloads.
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
  }, []);

  const fg = inverted ? 'var(--gb-bg)' : 'var(--gb-dark)';
  const bg = inverted ? 'var(--gb-deepest)' : 'var(--gb-bg)';

  return (
    <div style={{ marginTop: 3, display: 'flex', alignItems: 'center', gap: 4 }}>
      <button
        type="button"
        onClick={toggle}
        disabled={loading}
        data-testid="audio-play"
        aria-label={playing ? 'Pausar audio' : 'Reproducir audio'}
        style={{
          width: 18,
          height: 18,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: bg,
          color: fg,
          border: `1px solid ${fg}`,
          fontFamily: 'var(--pixel)',
          fontSize: 6,
          cursor: loading ? 'wait' : 'pointer',
          padding: 0,
        }}
      >
        {errored ? '!' : playing ? '||' : loading ? '..' : '>'}
      </button>
      <span
        style={{
          fontSize: 6,
          fontFamily: 'var(--pixel)',
          color: inverted ? 'var(--gb-bg)' : 'var(--gb-mid)',
          letterSpacing: 0.3,
        }}
      >
        {errored ? 'ERROR' : playing ? 'REPRODUCIENDO' : 'AUDIO'}
      </span>
    </div>
  );
}
